<template>
    <ModalContainer
        :isOpened="adDisabledModalOptions.isOpened"
        :wrapperClass="$style.wrapper_override"
        @closeModal="adDisabledStore.closeAdDisabledModal()"
    >
        <template #custom-content>
            <form
                :class="$style.content"
                @submit.prevent="onSubmit"
            >
                <img
                    :src="NoAdsImg"
                    alt="$tr('disabledAd.imageAlt')"
                    :class="$style.img"
                />
                <Typography
                    type="header"
                    size="s"
                    :responsive="false"
                >
                    {{ $tr('disabledAd.title') }}
                </Typography>
                <Typography
                    type="label"
                    size="s"
                    :responsive="false"
                    :class="$style.subtitle"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <span v-html="subtitle" />
                    <!-- eslint-enable vue/no-v-html -->
                </Typography>

                <div :class="$style.radio_group">
                    <Radio
                        v-for="duration in durations"
                        :key="duration.value"
                        :value="duration.value"
                        :label="duration.label"
                        :checked="duration.value === defaultChecked"
                    />
                </div>
                <Button
                    type="submit"
                    color="lime"
                    interfaceTarget="ad-disabled-modal-submit"
                    width="full"
                    size="l"
                >
                    {{ $tr('disabledAd.submit') }}
                </Button>
            </form>
        </template>
    </ModalContainer>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import Button from '@/components_new/Button.vue'
import ModalContainer from '@/components_new/ModalItems/ModalContainer.vue'
import Typography from '@/components_new/Typography.vue'
import { useConfig } from '@/composables/config'
import { useTr } from '@/composables/use-tr'
import { PRICE_IN_GAM, useAdDisabledStore } from '@/store/ad-disabled-store'
import { useUserStore } from '@/store/user-store/user-store'

import NoAdsImg from '../../images/no-ads.webp'
import Radio from './components/Radio.vue'

const config = useConfig()

const adDisabledStore = useAdDisabledStore()
const userStore = useUserStore()

const { user } = storeToRefs(userStore)
const { adDisabledModalOptions } = storeToRefs(adDisabledStore)

const tr = useTr()

const DURATION_OPTIONS = [1, 7, 30] as const

const durations = computed(() =>
    DURATION_OPTIONS.map((value) => {
        const priceInGam = PRICE_IN_GAM[value]
        const priceInUsd = priceInGam * +config.VUE_APP_GAM_TO_USD - 0.01

        return {
            value: value.toString(),
            label: user.value?.goldFenecBalance
                ? `${priceInGam} Gam = $${priceInUsd.toFixed(2)}`
                : tr('disabledAd.priceInUsd', { price: priceInUsd.toFixed(2) }),
        }
    }),
)

const subtitle = computed(() =>
    adDisabledModalOptions.value.isTopUp ? tr('disabledAd.subtitleExtend') : tr('disabledAd.subtitle'),
)

const defaultChecked = computed(() => {
    const balance = user.value?.goldFenecBalance || 0

    return DURATION_OPTIONS.findLast((d) => balance >= PRICE_IN_GAM[d])?.toString() || DURATION_OPTIONS[0]?.toString()
})

function onSubmit(e: Event) {
    const formData = new FormData(e.target as HTMLFormElement)
    const duration = formData.get('duration') as string
    adDisabledStore.disableAd(+duration as unknown as 1 | 7 | 30)
}
</script>

<style module>
.wrapper_override {
    background: linear-gradient(180deg, rgba(80, 255, 115, 1) 0%, rgba(52, 140, 120, 1) 22%, rgba(24, 24, 26, 1) 45%);
}

.content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 388px;
    max-width: 100%;
}

.img {
    width: 94px;
    height: 94px;
    margin-bottom: 24px;
}

.subtitle {
    text-align: center;
    margin: 8px 0 32px;
    color: var(--white-60);
}

.radio_group {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 32px;
}
</style>
